import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProjectsService } from '../services/projects.service';
import type { Project, ProjectFilters, ProjectStats } from '../types/projects.types';
import { useState, useEffect, useMemo } from 'react';

export function useProjects(companyId: string, initialFilters: ProjectFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ProjectFilters>({
    page: 1,
    pageSize: 10,
    status: 'all',
    type: 'all',
    ...initialFilters
  });

  const filterRef = JSON.stringify(initialFilters);
  useEffect(() => {
    setFilters(prev => ({ ...prev, ...initialFilters }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRef]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['projects', companyId, filters],
    queryFn: () => ProjectsService.getProjects(companyId, filters),
    enabled: !!companyId,
  });

  const { data: stats } = useQuery({
    queryKey: ['projects', 'stats', companyId],
    queryFn: () => ProjectsService.getProjectStats(companyId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (newProject: Partial<Project>) => ProjectsService.createProject(newProject),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<Project> }) => 
      ProjectsService.updateProject(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'detail', variables.id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ProjectsService.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  return {
    projects: data?.data || [],
    total: data?.total || 0,
    stats: stats || { total: 0, active: 0, planning: 0, completed: 0, totalBudget: 0, totalContract: 0 },
    isLoading,
    error,
    filters,
    setFilters,
    createProject: createMutation.mutateAsync,
    updateProject: updateMutation.mutateAsync,
    deleteProject: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

export function useProjectDetail(id?: string) {
  return useQuery({
    queryKey: ['projects', 'detail', id],
    queryFn: () => ProjectsService.getProjectDetail(id!),
    enabled: !!id,
  });
}
